//This page might reference TransferMarkt but we migrated it to VSN Index (Which is owned by VSN)
import React, { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../../../../apiFetch';
import './AdminVSNIndexReview.css';

const AdminVSNIndexReview = () => {
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showRejectModal, setShowRejectModal] = useState(null);
    const [rejectionReason, setRejectionReason] = useState('');
    const [canEditMap, setCanEditMap] = useState({});
    const [searchTerm, setSearchTerm] = useState('');

    // Toggles
    const [openDetails, setOpenDetails] = useState({});
    const [openRaw, setOpenRaw] = useState({});

    const safeJson = async (res) => {
        try {
            return await res.json();
        } catch {
            return null;
        }
    };

    useEffect(() => {
        const fetchApplications = async () => {
            try {
                // ✅ BACKEND ENDPOINT UNCHANGED
                const res = await apiFetch('/api/admin/transfermarkt-applications');
                if (!res.ok) throw new Error(await res.text());
                const data = await res.json();

                setApplications(data);

                const uniquePlayerIds = [...new Set(data.map((app) => app.playerId))];
                const playerCanEditStates = {};

                await Promise.all(
                    uniquePlayerIds.map(async (id) => {
                        const r = await apiFetch(`/api/player-details/${id}`);
                        if (r.ok) {
                            const playerData = await r.json();
                            // ✅ BACKEND FIELD UNCHANGED
                            playerCanEditStates[id] = playerData.canEditTransfermarkt ?? true;
                        } else {
                            playerCanEditStates[id] = true;
                        }
                    })
                );

                setCanEditMap(playerCanEditStates);
            } catch (err) {
                console.error('Error fetching applications or player data:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchApplications();
    }, []);

    const handleForward = async (applicationId) => {
        try {
            // ✅ BACKEND ENDPOINT UNCHANGED
            const res = await apiFetch(`/api/admin/forward-transfermarkt/${applicationId}`, {
                method: 'POST',
            });

            const data = await safeJson(res);
            alert(
                res.ok
                    ? 'Application successfully forwarded to VSN Index.'
                    : `Error: ${data?.error || 'Failed to forward application.'}`
            );

            if (res.ok) {
                setApplications((prev) =>
                    prev.map((a) => (a.id === applicationId ? { ...a, status: 'Forwarded' } : a))
                );
            }
        } catch (error) {
            console.error('Forwarding failed:', error);
            alert('Failed to forward application.');
        }
    };

    const handleUpdateLink = (playerId) => {
        const link = prompt('Paste the VSN Index profile link for this player:');
        if (!link) return;

        // ✅ BACKEND ENDPOINT UNCHANGED
        apiFetch(`/api/admin/update-transfermarkt-link/${playerId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ transfermarktLink: link }),
        })
            .then(async (res) => {
                const data = await safeJson(res);
                if (!res.ok) throw new Error(data?.error || 'Failed to update link.');
                alert('VSN Index link updated.');
            })
            .catch((err) => {
                console.error('Update failed:', err);
                alert('Failed to update VSN Index link.');
            });
    };

    const handleReject = async (applicationId) => {
        if (!rejectionReason.trim()) {
            alert('Please enter a rejection reason.');
            return;
        }

        try {
            // ✅ BACKEND ENDPOINT UNCHANGED
            const res = await apiFetch(`/api/admin/reject-transfermarkt-application/${applicationId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ rejectionReason }),
            });

            const data = await safeJson(res);

            if (res.ok) {
                alert('Application rejected and player notified.');
                setRejectionReason('');
                setShowRejectModal(null);

                setApplications((prev) =>
                    prev.map((a) => (a.id === applicationId ? { ...a, status: 'Rejected' } : a))
                );
            } else {
                alert(`Error: ${data?.error || 'Failed to reject application.'}`);
            }
        } catch (err) {
            console.error('Rejection failed:', err);
            alert('Failed to reject application.');
        }
    };

    const toggleCanEdit = async (playerId) => {
        const current = canEditMap[playerId] ?? true;

        try {
            // ✅ BACKEND ENDPOINT UNCHANGED
            const res = await apiFetch(`/api/admin/player/${playerId}/can-edit-transfermarkt`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ canEdit: !current }),
            });

            if (res.ok) {
                setCanEditMap((prev) => ({ ...prev, [playerId]: !current }));
            } else {
                const data = await safeJson(res);
                alert(data?.error || 'Failed to update permission');
            }
        } catch (err) {
            console.error('Error updating permission:', err);
            alert('Failed to update permission');
        }
    };

    const filteredApplications = useMemo(() => {
        return applications.filter((app) =>
            (app.fullName || '').toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [applications, searchTerm]);

    const fmt = (v) => {
        if (v === undefined || v === null) return 'N/A';
        const s = String(v).trim();
        return s.length ? s : 'N/A';
    };

    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            alert('Copied to clipboard.');
        } catch {
            alert('Copy failed (browser blocked).');
        }
    };

    if (loading) {
        return <p className="vsnindex-applications-review-loading">Loading VSN Index applications...</p>;
    }

    return (
        <div className="vsnindex-applications-review">
            <h2 className="vsnindex-title">VSN Index Applications</h2>

            <input
                type="text"
                placeholder="Search by player name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
            />

            {filteredApplications.length === 0 ? (
                <p className="vsnindex-empty">No applications match your search.</p>
            ) : (
                filteredApplications.map((app) => {
                    const isDetailsOpen = !!openDetails[app.id];
                    const isRawOpen = !!openRaw[app.id];

                    return (
                        <div className="application-wrapper" key={app.id}>
                            <div className="application-card">
                                <h3>{app.fullName}</h3>

                                <p>
                                    <strong>Status:</strong> {fmt(app.status)}
                                </p>

                                <p>
                                    <strong>Submitted:</strong>{' '}
                                    {app.createdAt ? new Date(app.createdAt).toLocaleDateString() : 'N/A'}
                                </p>

                                <label>
                                    <input
                                        type="checkbox"
                                        checked={canEditMap[app.playerId] ?? true}
                                        onChange={() => toggleCanEdit(app.playerId)}
                                    />
                                    {' '}Allow player to see "Add VSN Index" button
                                </label>

                                {/* ✅ Clean admin view (NOT JSON) */}
                                <div style={{ marginTop: 12 }}>
                                    <button
                                        type="button"
                                        className="vsnindex-details-toggle"
                                        onClick={() =>
                                            setOpenDetails((prev) => ({ ...prev, [app.id]: !prev[app.id] }))
                                        }
                                    >
                                        {isDetailsOpen ? 'Hide submitted details' : 'View submitted details'}
                                    </button>

                                    {isDetailsOpen && (
                                        <div className="vsnindex-details-panel">
                                            <p><strong>Player Email:</strong> {fmt(app.emailAddress || app.loginEmail || app.email)}</p>
                                            <p><strong>Date of Birth:</strong> {fmt(app.dob)}</p>
                                            <p><strong>Nationality:</strong> {fmt(app.nationality)}</p>

                                            <p><strong>Primary Position:</strong> {fmt(app.primaryPosition)}</p>
                                            <p><strong>Secondary Position:</strong> {fmt(app.secondaryPosition)}</p>

                                            <p><strong>Preferred Foot:</strong> {fmt(app.preferredFoot)}</p>
                                            <p><strong>Height:</strong> {app.height ? `${app.height}` : 'N/A'}</p>
                                            <p><strong>Weight:</strong> {app.weight ? `${app.weight}` : 'N/A'}</p>

                                            <p><strong>Current Club:</strong> {fmt(app.currentClub)}</p>
                                            <p><strong>Club Country:</strong> {fmt(app.clubCountry)}</p>
                                            <p><strong>Previous Club:</strong> {fmt(app.previousClub)}</p>

                                            <p><strong>League Name:</strong> {fmt(app.leagueName)}</p>
                                            <p><strong>Club Website:</strong> {fmt(app.clubWebsite)}</p>
                                            <p><strong>Agent Detail:</strong> {fmt(app.agentDetail)}</p>

                                            <p><strong>YouTube Link:</strong> {fmt(app.youtubeLink)}</p>

                                            <p><strong>VSN Profile Link:</strong> {fmt(app.profileLink)}</p>

                                            <p><strong>Introduction:</strong> {fmt(app.introduction)}</p>

                                            {/* Debug toggle stays separate */}
                                            <div style={{ marginTop: 12 }}>
                                                <button
                                                    type="button"
                                                    className="vsnindex-details-toggle"
                                                    onClick={() =>
                                                        setOpenRaw((prev) => ({ ...prev, [app.id]: !prev[app.id] }))
                                                    }
                                                >
                                                    {isRawOpen ? 'Hide debug payload' : 'Debug: raw application payload'}
                                                </button>

                                                {isRawOpen && (
                                                    <div style={{ marginTop: 10 }}>
                                                        <button
                                                            type="button"
                                                            className="vsnindex-copy-button"
                                                            onClick={() => copyToClipboard(JSON.stringify(app, null, 2))}
                                                        >
                                                            Copy raw payload
                                                        </button>

                                                        {/* ✅ This is intentionally “code-like” */}
                                                        <pre style={{ marginTop: 10, whiteSpace: 'pre-wrap' }}>
                                                            {JSON.stringify(app, null, 2)}
                                                        </pre>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="application-buttons">
                                <button onClick={() => handleForward(app.id)}>Forward to VSN Index</button>
                                <button onClick={() => setShowRejectModal(app.id)}>Reject Application</button>
                                <button onClick={() => handleUpdateLink(app.playerId)}>Update VSN Index Link</button>
                            </div>

                            {showRejectModal === app.id && (
                                <div className="modal-overlay">
                                    <div className="modal-content">
                                        <h3>Rejection Reason</h3>
                                        <textarea
                                            value={rejectionReason}
                                            onChange={(e) => setRejectionReason(e.target.value)}
                                            placeholder="Enter rejection reason..."
                                        />
                                        <div style={{ display: 'flex', gap: '10px' }}>
                                            <button onClick={() => handleReject(app.id)}>Submit</button>
                                            <button onClick={() => setShowRejectModal(null)}>Cancel</button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })
            )}
        </div>
    );
};

export default AdminVSNIndexReview;




